import React, { useEffect, useState } from "react";
import { Bell, BellOff, Loader2 } from "lucide-react";
import { api } from "../lib/api";

/* Compact "Get alerts" pill that sits inline in the header next to the
   Buy-me-a-coffee button. States cycled through:
     - "unsupported"  → don't render
     - "denied"       → don't render (browser permanently rejected)
     - "default"      → render "Bell · Get alerts" button
     - "subscribing"  → spinner
     - "subscribed"   → muted "Bell · On" with click-to-unsubscribe
   On iOS, push only works in PWA standalone mode. When NOT in standalone
   we still render a discreet pill that opens an explainer instead of
   triggering an immediately-failing request. */

const SUB_KEY = "last:push:endpoint";

function urlBase64ToUint8Array(s) {
  const padding = "=".repeat((4 - (s.length % 4)) % 4);
  const b64 = (s + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}
function bufToB64(buf) {
  const bytes = new Uint8Array(buf);
  let s = "";
  for (let i = 0; i < bytes.byteLength; i += 1) s += String.fromCharCode(bytes[i]);
  return btoa(s);
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  try {
    if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
  } catch (_) {}
  return !!window.navigator?.standalone;
}
function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

export function PushOptInPill({ alwaysShow = false, large = false }) {
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState("default");
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const ok = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
    setSupported(ok);
    if (!ok) return;
    setPermission(Notification.permission);
    // Are we already subscribed? Cheap synchronous-ish check.
    navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then((sub) => setSubscribed(!!sub))
      .catch(() => {});
  }, []);

  const handleSubscribe = async () => {
    if (busy) return;
    // iOS Safari only ships push to PWAs installed to the home screen.
    if (isIOS() && !isStandalone()) {
      setIosHint(true);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.get("/push/public-key");
      const publicKey = data?.public_key;
      if (!publicKey) throw new Error("VAPID key missing");
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") return;
      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        });
      }
      await api.post("/push/subscribe", {
        endpoint: sub.endpoint,
        keys: {
          p256dh: bufToB64(sub.getKey("p256dh")),
          auth: bufToB64(sub.getKey("auth")),
        },
      });
      try { window.localStorage.setItem(SUB_KEY, sub.endpoint); } catch (_) {}
      setSubscribed(true);
    } catch (e) {
      // Soft-fail — the pill simply stays in "default" state for retry.
      // eslint-disable-next-line no-console
      console.warn("push subscribe failed", e);
    } finally {
      setBusy(false);
    }
  };

  const handleUnsubscribe = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.post("/push/unsubscribe", { endpoint: sub.endpoint });
        await sub.unsubscribe();
      }
      try { window.localStorage.removeItem(SUB_KEY); } catch (_) {}
      setSubscribed(false);
    } finally {
      setBusy(false);
    }
  };

  if (!supported || permission === "denied") return null;

  // Show on every breakpoint when `alwaysShow` is set (used by the
  // site-wide action bar). Header usage keeps the pill desktop-only so
  // the mobile chrome stays small.
  const sizingClass = alwaysShow ? "inline-flex" : "hidden sm:inline-flex";
  // Responsive shape: 40×40 circle on phones (matches the Share /
  // Search / social circles), pill with label on ≥sm. Combined into a
  // single button so we only have one push-permission listener instance.
  const shapeClass = large
    ? "h-11 w-11 justify-center px-0 py-0 sm:h-auto sm:w-auto sm:justify-start sm:px-3 sm:py-1.5"
    : "h-6 w-6 justify-center px-0 py-0 sm:h-auto sm:w-auto sm:justify-start sm:px-3 sm:py-1.5";
  const iconSize = large ? 20 : 16;

  // iOS-not-installed prompt — a small inline link that explains the
  // home-screen requirement. Hidden once dismissed.
  if (iosHint) {
    return (
      <button
        type="button"
        onClick={() => setIosHint(false)}
        data-testid="push-pill-ios-hint"
        title="Add this site to your home screen first via the share menu."
        aria-label="Add to home screen first for push notifications"
        className={`${sizingClass} ${shapeClass} items-center gap-1.5 rounded-full border border-black/15 bg-white font-display text-xs font-bold text-[var(--brand-ink)] hover:border-[#0A0F17]`}
      >
        <Bell size={iconSize} strokeWidth={2.5} className="sm:hidden" />
        <Bell size={12} strokeWidth={2.5} className="hidden sm:inline" />
        <span className="hidden sm:inline">Add to home screen first</span>
      </button>
    );
  }

  if (subscribed) {
    return (
      <button
        type="button"
        onClick={handleUnsubscribe}
        disabled={busy}
        data-testid="push-pill-unsubscribe"
        title="You'll get a push notification for new blog posts. Click to turn off."
        aria-label="Alerts on — click to unsubscribe"
        className={`${sizingClass} ${shapeClass} items-center gap-1.5 rounded-full border border-[#0A0F17] bg-[#0A0F17] font-display text-xs font-bold text-white hover:bg-[var(--brand-accent)] hover:text-[var(--brand-ink)]`}
      >
        {busy ? (
          <>
            <Loader2 size={iconSize} className="animate-spin sm:hidden" />
            <Loader2 size={12} className="hidden animate-spin sm:inline" />
          </>
        ) : (
          <>
            <Bell size={iconSize} strokeWidth={2.5} className="sm:hidden" />
            <Bell size={12} strokeWidth={2.5} className="hidden sm:inline" />
          </>
        )}
        <span className="hidden sm:inline">Alerts on</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleSubscribe}
      disabled={busy}
      data-testid="push-pill-subscribe"
      title="Get a push notification when a new post drops."
      aria-label="Get alerts — subscribe to push notifications"
      className={`${sizingClass} ${shapeClass} items-center gap-1.5 rounded-full border border-black/15 bg-white font-display text-xs font-bold text-[var(--brand-ink)] hover:border-[#0A0F17]`}
    >
      {busy ? (
        <>
          <Loader2 size={iconSize} className="animate-spin sm:hidden" />
          <Loader2 size={12} className="hidden animate-spin sm:inline" />
        </>
      ) : (
        <>
          <Bell size={iconSize} strokeWidth={2.5} className="sm:hidden" />
          <Bell size={12} strokeWidth={2.5} className="hidden sm:inline" />
        </>
      )}
      <span className="hidden sm:inline">Get alerts</span>
    </button>
  );
}

export default PushOptInPill;
